<template>
  <div>
    <button
      :style="{ top: btnPos.top + 'px', right: btnPos.right + 'px' }"
      type="button"
      class="schema-inspection-trigger"
      :class="[
        disabled ? '' : animationClass,
        {
          disabled,
          moving: isMoving
        }
      ]"
      ref="schemaInspection"
      :disabled="disabled"
      @animationend="onAnimationEnd"
      @keydown.enter.prevent="onClick"
      @keydown.space.prevent="onClick"
    >
      <span class="blur"></span>
      <span class="title">{{ $t('observedSitemap.inspection.inspect') }}</span>
      <span v-if="disabled" class="report-no-data">NO DATA</span>
      <span v-else class="desc">{{ description }}</span>
    </button>
  </div>
</template>

<script>
import { makeDraggable, normalizeTopRightPosition } from '@/libs/draggable'

export default {
  name: 'BaSchemaInspectionFloatingButton',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    requested: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      animationClass: 'ld-bounce-in',
      isMoving: false,
      removeDraggable: null,
      btnPos: { top: 0, right: 16 }
    }
  },
  computed: {
    minTop () {
      const paneWrap = document.querySelector('.ba-sitemap .pane-wrap')
      return paneWrap ? paneWrap.getBoundingClientRect().top : 0
    },
    description () {
      return this.requested
        ? this.$t('observedSitemap.inspection.requested')
        : this.$t('observedSitemap.inspection.defaultDescription')
    }
  },
  mounted () {
    this.setDefaultPosition()
    this.initDraggable()
    window.addEventListener('resize', this.onBrowserResize)
  },
  beforeDestroy () {
    if (this.removeDraggable) this.removeDraggable()
    window.removeEventListener('resize', this.onBrowserResize)
  },
  methods: {
    setDefaultPosition () {
      const btnEl = this.$refs.schemaInspection
      if (!btnEl) return

      this.$nextTick(() => {
        const defaultTop = document.documentElement.clientHeight - btnEl.offsetHeight - 16

        this.btnPos = {
          top: Math.max(this.minTop, defaultTop),
          right: 16
        }
      })
    },
    initDraggable () {
      const btnEl = this.$refs.schemaInspection
      if (!btnEl) return

      this.removeDraggable = makeDraggable(btnEl, {
        getPosition: () => this.btnPos,
        setPosition: position => {
          this.btnPos = position
        },
        minTop: () => this.minTop,
        onClick: this.onClick,
        onMoveStart: () => {
          this.isMoving = true
        },
        onMoveEnd: () => {
          this.isMoving = false
        }
      })
    },
    onBrowserResize () {
      const btnEl = this.$refs.schemaInspection
      if (!btnEl) return

      this.btnPos = normalizeTopRightPosition(btnEl, this.btnPos, {
        minTop: this.minTop
      })
    },
    onClick () {
      if (!this.disabled) {
        this.$emit('click')
      }
    },
    onAnimationEnd (event) {
      if (event.animationName === 'ld-bounce-in') {
        this.animationClass = 'ld-bounce-in2 infinite-bounce'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.schema-inspection-trigger {
  width: 132px;
  height: 132px;
  border-radius: 14px;
  background: linear-gradient(94deg, #0085ff 0%, #0058dd 100%);
  box-shadow: 0 38px 38px -10px rgba(0, 0, 0, 0.44);
  transition: scale 0.5s ease-in-out;
  position: fixed;
  right: 16px;
  z-index: 100;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 14px 12px;
  gap: 5px;

  &.ld-bounce-in {
    animation: ld-bounce-in 1s 1 linear;
  }

  &:not(:hover) {
    &.infinite-bounce {
      animation: ld-bounce-in2 5s linear infinite;
    }
  }

  &:after {
    content: '';
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 80px;
    background: url('../../assets/img/common/chatbot-normal.svg?url') no-repeat center / contain;
  }

  &:before {
    content: '';
    width: 52px;
    height: 4px;
    border-radius: 50em;
    background-color: #fff;
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
  }

  &:hover {
    scale: 1.1;

    &:after {
      background-image: url('../../assets/img/common/chatbot-hover.svg?url');
    }

    &:before {
      opacity: 1;
    }

    .blur {
      opacity: 1;
    }
  }

  &.moving {
    transition: none;
    scale: 1.1;
  }

  &.disabled {
    background: linear-gradient(94deg, #a8d1fb 0%, #a2c1f2 100%);
    box-shadow: 0 38px 38px -10px rgba(0, 0, 0, 0.12);
    cursor: default;
    scale: 1;
    padding-bottom: 8px;

    &:after {
      background-image: url('../../assets/img/common/chatbot-disabled.svg');
    }

    &:hover {
      scale: 1;

      &:after {
        background-image: url('../../assets/img/common/chatbot-disabled.svg');
      }
    }

    .blur {
      background-color: #e9f7ff;
      opacity: 0.6;
      filter: blur(24px);
    }
  }

  .blur {
    transition: opacity 0.3s ease-in-out;
    opacity: 0;
    width: 90px;
    height: 90px;
    background-color: #4cb1ff;
    filter: blur(24px);
    position: absolute;
    border-radius: 50em;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: -1;
  }

  .title {
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
  }

  .desc {
    color: #badfff;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
  }

  .report-no-data {
    border-radius: 999px;
    background: #fff;
    display: flex;
    width: 116px;
    height: 20px;
    padding: 0 33px;
    justify-content: center;
    align-items: center;
    color: #777;
    font-size: 11px;
    font-weight: 600;
    line-height: 100%;
    box-sizing: border-box;
  }
}

@keyframes ld-bounce-in {
  0% {
    animation-timing-function: cubic-bezier(0.0383, 0.3388, 0.0421, 0.6652);
    transform: matrix(0.01, 0, 0, 0.01, 0, 0);
    opacity: 0;
  }
  4.7% {
    animation-timing-function: cubic-bezier(0.3296, 0.3336, 0.5772, 0.6672);
    transform: matrix(0.556, 0, 0, 0.556, 0, 0);
    opacity: 0.1;
  }
  8.4% {
    animation-timing-function: cubic-bezier(0.0781, 0.1617, 0.0874, 0.9301);
    transform: matrix(1.002, 0, 0, 1.002, 0, 0);
    opacity: 0.2;
  }
  14.11% {
    animation-timing-function: cubic-bezier(0.3071, 0.3115, 0.5563, 1.0037);
    transform: matrix(0.936, 0, 0, 0.936, 0, 0);
    opacity: 0.39;
  }
  17.82% {
    animation-timing-function: cubic-bezier(0.1259, 0.2548, 0.1241, 0.9968);
    transform: matrix(1, 0, 0, 1, 0, 0);
    opacity: 0.53;
  }
  24.32% {
    animation-timing-function: cubic-bezier(0.2598, 0.3517, 0.4995, 1.2445);
    transform: matrix(0.987, 0, 0, 0.987, 0, 0);
    opacity: 0.74;
  }
  25.23% {
    animation-timing-function: cubic-bezier(0.2265, 0.3321, 0.3804, 1.1878);
    transform: matrix(0.989, 0, 0, 0.989, 0, 0);
    opacity: 0.77;
  }
  32.43% {
    animation-timing-function: cubic-bezier(0.1648, 0.5132, 0.2623, 1.0033);
    transform: matrix(1, 0, 0, 1, 0, 0);
    opacity: 1;
  }
  100% {
    transform: matrix(1, 0, 0, 1, 0, 0);
    opacity: 1;
  }
}

@keyframes ld-bounce-in2 {
  0%, 100% {
    transform: matrix(1, 0, 0, 1, 0, 0);
  }
  3.7% {
    transform: matrix(1.07, 0, 0, 1.07, 0, 0);
  }
  7.4% {
    transform: matrix(0.98, 0, 0, 0.98, 0, 0);
  }
  11.1% {
    transform: matrix(1.02, 0, 0, 1.02, 0, 0);
  }
  14.8% {
    transform: matrix(0.99, 0, 0, 0.99, 0, 0);
  }
  18.5% {
    transform: matrix(1, 0, 0, 1, 0, 0);
  }
}
</style>
